import { Button, Container } from '@cgs/wge';
import { T_ISpinController } from '../../../type_definitions';
import { ISpinController } from '../../../game/common/i_spin_controller';
import { getCheatUIStyles } from './styles';

export const CheatPopupToken = Symbol('CheatPopup');
export const CheatPopupElementToken = Symbol('CheatPopupElement');

const POSITION_STORAGE_KEY = 'cheat_panel_position';

export class CheatPopup {
  private consoleEl: HTMLDivElement | null = null;

  private isShown: boolean = false;
  private isCollapsed: boolean = false;

  constructor(private container: Container) {
    this.initialize();
  }

  public initialize(): void {
    this.consoleEl = document.createElement('div');

    const styles = getCheatUIStyles();

    this.consoleEl.style = styles.popup;

    this.restoreConsolePosition();

    this.consoleEl.innerHTML = `
      <div class="cheat-header" style="${styles.headerStyle}">
        <span style="color: white; font-size: 14px;">Cheat Console</span>
        <div style="${styles.buttonContainerStyle}">
          <button id="collapseBtn" style="${styles.headerButtonStyle}" title="Collapse">−</button>
          <button id="closeBtn" style="${styles.headerButtonStyle}" title="Close">×</button>
        </div>
      </div>
    `;

    const content = document.createElement('div');
    content.id = 'cheat-content';

    Object.assign(content.style, {
      maxHeight: '80vh',
      overflowY: 'auto',
      padding: '16px',
    });

    // Create a style element for WebKit scrollbar styles
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      #cheat-content::-webkit-scrollbar { width: 8px; }
      #cheat-content::-webkit-scrollbar-track { background: rgba(0,0,0,0.1); }
      #cheat-content::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 4px; }
      #cheat-content::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
    `;

    document.head.appendChild(styleElement);

    this.consoleEl.appendChild(content);

    this.container.registerValue(CheatPopupElementToken, content);

    document.body.appendChild(this.consoleEl);

    // Add drag functionality
    this.initializeDrag();
    this.setupEventListeners();

    document.addEventListener('keydown', (e) => this.handleKeyDown(e));
  }

  public addSpinLongPressedListener(): void {
    const spinController = this.container.resolve<ISpinController>(T_ISpinController, {
      optional: true,
    });
    spinController?.spinBtn.forEach((spinBtn: Button) =>
      spinBtn.longPressed.listen(() => this.toggleShownState())
    );
  }

  private initializeDrag(): void {
    if (!this.consoleEl) return;

    const header = this.consoleEl.querySelector('.cheat-header');
    if (!header) return;

    let isDragging = false;
    let startX: number;
    let startY: number;

    const onMouseDown = ((e: Event) => {
      const mouseEvent = e as MouseEvent;
      isDragging = true;
      startX = mouseEvent.clientX - this.consoleEl!.offsetLeft;
      startY = mouseEvent.clientY - this.consoleEl!.offsetTop;
      this.consoleEl!.style.cursor = 'grabbing';
    }) as EventListener;

    const onMouseMove = ((e: Event) => {
      if (!isDragging || !this.consoleEl) return;
      const mouseEvent = e as MouseEvent;
      const x = mouseEvent.clientX - startX;
      const y = mouseEvent.clientY - startY;

      this.consoleEl.style.left = `${x}px`;
      this.consoleEl.style.top = `${y}px`;
      this.consoleEl.style.transform = 'none';
    }) as EventListener;

    const onMouseUp = (() => {
      if (isDragging) {
        isDragging = false;
        if (this.consoleEl) {
          this.consoleEl.style.cursor = 'default';
          this.saveConsolePosition();
        }
      }
    }) as EventListener;

    header.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  private setupEventListeners(): void {
    if (!this.consoleEl) return;

    // Add collapse/close functionality
    this.consoleEl
      .querySelector('#collapseBtn')
      ?.addEventListener('click', () => this.toggleCollapse());

    this.consoleEl
      .querySelector('#closeBtn')
      ?.addEventListener('click', () => this.toggleShownState());
  }

  private toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    const content = this.consoleEl?.querySelector<HTMLDivElement>('#cheat-content');
    if (content) {
      content.style.display = this.isCollapsed ? 'none' : 'block';
      const collapseBtn = this.consoleEl?.querySelector<HTMLButtonElement>('#collapseBtn');
      if (collapseBtn) {
        collapseBtn.textContent = this.isCollapsed ? '+' : '−';
      }
    }
  }

  public show(): void {
    this.setShowState(true);
  }

  private toggleShownState(): void {
    this.setShowState(!this.isShown);
  }

  private setShowState(show: boolean): void {
    this.isShown = show;
    if (this.consoleEl) {
      this.consoleEl.style.display = show ? 'block' : 'none';
    }
  }

  private saveConsolePosition(): void {
    if (this.consoleEl) {
      const rect = this.consoleEl.getBoundingClientRect();
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: rect.left, y: rect.top }));
    }
  }

  private restoreConsolePosition(): void {
    const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
    if (savedPosition && this.consoleEl) {
      try {
        const { x, y } = JSON.parse(savedPosition);
        this.consoleEl.style.left = `${x}px`;
        this.consoleEl.style.top = `${y}px`;
        this.consoleEl.style.transform = 'none';
      } catch (e) {
        console.error('Error restoring console position:', e);
      }
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'u' || event.key === 'U') {
      this.toggleShownState();
    }
  }
}
